import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import EventCard from './eventCard';
import './events.css';

const HOME_DESKTOP_EVENT_COUNT = 6;
const THREE_COLUMN_BREAKPOINT = 1101;
const CARD_WIDTH = 300;
const CARD_GAP = 50;

// Markdown画像のタイトル("...")でサイズを指定できる: small=控えめ, logo=ロゴ用
const IMAGE_WIDTHS = { small: '340px', logo: '240px' };

// 段落まるごとが太字1つだけ（＝「——」で始まるインタビューの質問行）を判定し、
// q-line クラスを付ける。回答文中のインライン太字は対象外（地の文が混ざるため）。
const isQuestionParagraph = (node) => {
  const kids = (node?.children || []).filter(
    (c) => !(c.type === 'text' && !c.value.trim())
  );
  return kids.length === 1 && kids[0].type === 'element' && kids[0].tagName === 'strong';
};

const markdownComponents = {
  p: ({ node, children, ...props }) => (
    <p className={isQuestionParagraph(node) ? 'q-line' : undefined} {...props}>
      {children}
    </p>
  ),
  img: ({ node, title, ...props }) => (
    <div style={{ textAlign: 'center' }}>
      <img
        {...props}
        alt={props.alt}
        style={{
          maxWidth: '100%',
          width: IMAGE_WIDTHS[title] || '500px',
          maxHeight: 'auto',
          height: 'auto',
          objectFit: 'contain',
          margin: '20px auto 0',
          borderRadius: '8px'
        }}
      />
      {title !== 'logo' && props.alt && (
        <div className="event-image-caption">{props.alt}</div>
      )}
    </div>
  )
};

function EventsList({ selectedId, category }) {
  const containerRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3);
  const navigate = useNavigate();
  const location = useLocation(); // ← 現在のURLパスを取得

  // category: 'interview'=interviews.json / それ以外=events.json を読む
  const dataUrl = category === 'interview' ? '/interviews.json' : '/events.json';
  const listBasePath = category === 'interview' ? '/interviews' : '/allevents';

  useEffect(() => {
    fetch(dataUrl)
      .then(res => res.json())
      .then(data => setEvents(data))
      .catch(err => console.error('Failed to load events:', err));
  }, [dataUrl]);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (containerRef.current) {
        // 一覧ページ（/allevents・/interviews）のときだけ全表示
        if (location.pathname === '/allevents' || location.pathname === '/interviews') {
          setVisibleCount(events.length);
        } else if (window.innerWidth >= THREE_COLUMN_BREAKPOINT) {
          setVisibleCount(Math.min(HOME_DESKTOP_EVENT_COUNT, events.length));
        } else {
          const containerWidth = containerRef.current.offsetWidth;
          const maxVisible = Math.max(
            1,
            Math.floor((containerWidth + CARD_GAP) / (CARD_WIDTH + CARD_GAP))
          );
          setVisibleCount(Math.min(maxVisible, events.length));
        }
      }
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, [events.length, location.pathname]);

  const selectedEvent = selectedId
    ? events.find(e => e['event-id'] === selectedId)
    : null;

  const handleCardClick = (id) => {
    navigate(`${listBasePath}/${id}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <div className="events-list-container">
      {!selectedEvent && (
        <div className="events-list" ref={containerRef}>
          {events.slice(0, visibleCount).map((event) => (
            <EventCard
              key={event['event-id']}
              imageUrl={event.imageUrl}
              tags={event.tags || []}
              title={event.title}
              details={event.details}
              content={event.content}
              eventDate={event['event-date']}
              onClick={() => handleCardClick(event['event-id'])}
            />
          ))}
        </div>
      )}

      {selectedEvent && (
        <div className="event-detail">
          <div className="event-detail-meta">
            <span className="event-detail-date">{selectedEvent['event-date']}</span>
            {(selectedEvent.tags || []).map(tag => (
              <span key={tag} className="event-detail-tag">{tag}</span>
            ))}
          </div>
          <div className="event-detail-body">
            <ReactMarkdown components={markdownComponents}>
              {selectedEvent.content}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventsList;
